import React from 'react'
import { Form as FinalForm } from 'react-final-form'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import { Button, Grid, makeStyles, Typography } from '@material-ui/core'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import FiledDraggable from './FiledDraggable'
import useTranslate from '../../../i18n/useTranslate'

import { ApproveStatusType } from '../../../pages/SettingConsideration'
import { ConsiderationDataListType } from '../../../services/um/useSearchSettingConsideration'

const getListStyle = (isDraggingOver: boolean): React.CSSProperties => ({
    margin: '16px 0px',
    width: '100%',
})

type DraggablesProps = {
    handleOrder: (value: ApproveStatusType) => void
    isDragDisabled: boolean
    handleOrderNumberMode: () => void
    dataList?: ConsiderationDataListType[]
}

const Draggables = ({ handleOrder, isDragDisabled, handleOrderNumberMode, dataList }: DraggablesProps): JSX.Element => {
    const classes = useStyles()
    const translate = useTranslate()

    const onDragEnd = (fields: any, form: any) => (result: DropResult) => {
        if (!result.destination) {
            return
        }

        const items: ConsiderationDataListType[] = reorder(fields.value, result.source.index, result.destination.index)
        form.change('approveStatus', items)
    }

    const reorder = (
        list: ConsiderationDataListType[],
        startIndex: number,
        endIndex: number,
    ): ConsiderationDataListType[] => {
        const result = Array.from(list)
        const [removed] = result.splice(startIndex, 1)
        result.splice(endIndex, 0, removed)

        return result
    }

    const initialValues = {
        approveStatus: dataList,
    }

    return (
        <FinalForm
            onSubmit={handleOrder}
            initialValues={initialValues}
            mutators={{
                ...arrayMutators,
            }}
            render={({ handleSubmit, form, submitting, pristine }) => (
                <form onSubmit={handleSubmit}>
                    <>
                        <Grid container justify="space-between" style={{ margin: '15px 0px' }}>
                            <Grid container item xs={12} sm={12} md={6} lg={6}>
                                <Grid item>
                                    <Typography variant="h6">
                                        {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList?.length ?? 0}`])}
                                    </Typography>
                                </Grid>
                                {!isDragDisabled && (
                                    <Grid container item xs={12} sm={12} md={12} lg={12}>
                                        <Grid item xs={12}>
                                            <Typography variant={'subtitle2'} style={{ color: 'red' }}>
                                                * {translate.get('LABEL.DRAGABLE_LABEL')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <Grid container item justify="flex-end" spacing={2}>
                                    {isDragDisabled ? (
                                        <Grid item>
                                            <Button
                                                fullWidth
                                                size="large"
                                                variant="outlined"
                                                color="primary"
                                                onClick={handleOrderNumberMode}
                                            >
                                                <Typography variant="button">
                                                    {translate.get('BUTTON.ORDER')}
                                                </Typography>
                                            </Button>
                                        </Grid>
                                    ) : (
                                        <>
                                            <Grid item>
                                                <Button
                                                    size="large"
                                                    variant="outlined"
                                                    className={classes.cancelButton}
                                                    onClick={() => {
                                                        handleOrderNumberMode?.()
                                                        return form.reset
                                                    }}
                                                    // disabled={submitting || pristine}
                                                >
                                                    {translate.get('BUTTON.CANCEL')}
                                                </Button>
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    size={'large'}
                                                    variant="contained"
                                                    color="primary"
                                                    style={{ minWidth: '0px' }}
                                                    onClick={handleSubmit}
                                                    disabled={submitting || pristine}
                                                >
                                                    {translate.get('BUTTON.SAVE')}
                                                </Button>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <div className={classes.controlFieldArray}>
                            <FieldArray name="approveStatus">
                                {({ fields }) => (
                                    <DragDropContext onDragEnd={onDragEnd(fields, form)}>
                                        <Droppable droppableId="droppable">
                                            {(provided, snapshot): JSX.Element => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot.isDraggingOver)}
                                                >
                                                    <FiledDraggable
                                                        dataList={fields.value}
                                                        isDragDisabled={isDragDisabled}
                                                    />
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </FieldArray>
                        </div>
                    </>
                </form>
            )}
        />
    )
}

const useStyles = makeStyles((theme) => ({
    controlFieldArray: {
        width: '100%',
        overflowY: 'auto',
        [theme.breakpoints.down('md')]: {
            height: 'auto',
        },
        marginTop: '-15px',
    },
    button: {
        marginTop: '16px',
    },
    cancelButton: {
        backgroundColor: '#FFFFFF',
        border: 'solid 1px #db5f8e',
    },
}))

export default Draggables
